import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { SxProps, Theme } from '@mui/material/styles';

export interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  showCloseButton?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  role?: 'dialog' | 'alertdialog';
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  actions,
  showCloseButton = true,
  maxWidth = false,
  fullWidth = true,
  ariaLabelledBy,
  ariaDescribedBy,
  role,
  sx,
  contentSx,
  actionsSx,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
}) => {
  const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return;
    }
    onClose?.();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      aria-labelledby={ariaLabelledBy || (title ? 'modal-title' : undefined)}
      aria-describedby={ariaDescribedBy}
      PaperProps={role ? { role } : undefined}
      sx={{
        '& .MuiDialog-paper': {
          width: '440px',
          maxWidth: '440px',
        },
        ...sx,
      }}
      data-testid="modal"
    >
      {/* Title */}
      {title && (
        <DialogTitle
          id={ariaLabelledBy || 'modal-title'}
          sx={{
            m: '0 0 24px 0',
          }}
        >
          {title}
        </DialogTitle>
      )}

      {/* Close button (absolute positioned) */}
      {showCloseButton && onClose && (
        <IconButton
          aria-label="close"
          onClick={onClose}
          data-testid="modal-close-button"
        >
          <CloseIcon />
        </IconButton>
      )}

      {/* Content */}
      <DialogContent sx={{ margin: '0 0 32px 0', ...contentSx }}>
        {children}
      </DialogContent>

      {/* Actions */}
      {actions && (
        <DialogActions sx={{ gap: 1, ...actionsSx }}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;